import React, { useState } from "react";
import { makeStyles } from "tss-react/mui";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const useTraceStyles = makeStyles()((theme) => {
  const dark = theme.palette.mode === "dark";
  return {
    traceEmpty: {
      color: theme.palette.text.secondary,
      fontSize: 13,
    },
    traceList: {
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },
    traceEntry: {
      border: `1px solid ${dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
      borderRadius: 8,
      padding: 12,
      backgroundColor: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    },
    traceHeader: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
      cursor: "pointer",
      userSelect: "none",
    },
    traceHeaderOpen: {
      marginBottom: 6,
    },
    traceExpandIcon: {
      transition: "transform .15s ease",
      opacity: 0.6,
      flexShrink: 0,
    },
    traceExpandIconOpen: {
      transform: "rotate(180deg)",
    },
    traceDetail: {
      paddingTop: 2,
    },
    traceIndex: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 20,
      height: 20,
      borderRadius: "50%",
      fontSize: 11,
      fontWeight: 700,
      backgroundColor: dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
      color: theme.palette.text.secondary,
    },
    traceName: {
      fontWeight: 700,
      fontSize: 13.5,
      fontFamily: "monospace",
      flex: 1,
      minWidth: 120,
      wordBreak: "break-word",
    },
    traceChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "1px 8px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      lineHeight: "18px",
      textTransform: "uppercase",
      letterSpacing: 0.3,
    },
    kind_webhook: {
      backgroundColor: dark ? "rgba(66,165,245,0.18)" : "rgba(33,150,243,0.12)",
      color: dark ? "#82c4ff" : "#0d64b3",
    },
    kind_specialist: {
      backgroundColor: dark ? "rgba(171,71,188,0.2)" : "rgba(156,39,176,0.12)",
      color: dark ? "#e1a3ee" : "#7b1fa2",
    },
    kind_transfer: {
      backgroundColor: dark ? "rgba(255,167,38,0.2)" : "rgba(255,152,0,0.14)",
      color: dark ? "#ffc177" : "#a35a00",
    },
    statusOk: {
      backgroundColor: dark ? "rgba(102,187,106,0.2)" : "rgba(76,175,80,0.14)",
      color: dark ? "#8be08f" : "#2e7d32",
    },
    statusError: {
      backgroundColor: dark ? "rgba(239,83,80,0.22)" : "rgba(244,67,54,0.14)",
      color: dark ? "#ff9d99" : "#c62828",
    },
    traceDuration: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      marginLeft: "auto",
    },
    traceTarget: {
      fontFamily: "monospace",
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      wordBreak: "break-all",
      marginBottom: 8,
    },
    traceBlock: {
      marginTop: 8,
      "&:first-of-type": { marginTop: 0 },
    },
    traceBlockLabel: {
      fontSize: 10.5,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      color: theme.palette.text.secondary,
      marginBottom: 3,
    },
    traceBlockBody: {
      margin: 0,
      padding: 8,
      borderRadius: 6,
      fontSize: 12,
      lineHeight: 1.45,
      fontFamily: "monospace",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      maxHeight: 260,
      overflow: "auto",
      backgroundColor: dark ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0.04)",
      color: theme.palette.text.primary,
    },
    traceBlockError: {
      backgroundColor: dark ? "rgba(239,83,80,0.14)" : "rgba(244,67,54,0.08)",
      color: dark ? "#ff9d99" : "#c62828",
    },
  };
});

const KIND_LABELS = { webhook: "Webhook", specialist: "Especialista", transfer: "Transferencia" };

const TraceBlock = ({ label, value, error }) => {
  const { classes, cx } = useTraceStyles();
  if (value === undefined || value === null || value === "") return null;
  const text = typeof value === "string" ? value : JSON.stringify(value, null, 2);
  return (
    <div className={classes.traceBlock}>
      <div className={classes.traceBlockLabel}>{label}</div>
      <pre className={cx(classes.traceBlockBody, error && classes.traceBlockError)}>{text}</pre>
    </div>
  );
};

const ToolTraceView = ({ toolTrace }) => {
  const { classes, cx } = useTraceStyles();
  const [openIndexes, setOpenIndexes] = useState(() => new Set());

  if (!toolTrace?.length) {
    return <div className={classes.traceEmpty}>No se llamó ninguna herramienta en este turno.</div>;
  }

  const toggle = (i) => {
    setOpenIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  return (
    <div className={classes.traceList}>
      {toolTrace.map((t, i) => {
        const open = openIndexes.has(i);
        return (
          <div key={i} className={classes.traceEntry}>
            <div
              className={cx(classes.traceHeader, open && classes.traceHeaderOpen)}
              onClick={() => toggle(i)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggle(i);
                }
              }}
            >
              <ExpandMoreIcon
                fontSize="small"
                className={cx(classes.traceExpandIcon, open && classes.traceExpandIconOpen)}
              />
              <span className={classes.traceIndex}>{i + 1}</span>
              <span className={cx(classes.traceChip, classes[`kind_${t.kind}`])}>
                {KIND_LABELS[t.kind] || t.kind}
              </span>
              <span className={classes.traceName}>{t.name}</span>
              <span className={cx(classes.traceChip, t.ok ? classes.statusOk : classes.statusError)}>
                {t.ok ? "OK" : "Error"}
                {typeof t.status === "number" ? ` ${t.status}` : ""}
              </span>
              <span className={classes.traceDuration}>{t.durationMs} ms</span>
            </div>
            {open && (
              <div className={classes.traceDetail}>
                <div className={classes.traceTarget}>{t.target}</div>
                <TraceBlock label="Args" value={t.args} />
                <TraceBlock label="Resultado" value={t.result} />
                <TraceBlock label="Respuesta cruda" value={t.raw} />
                <TraceBlock label="Error" value={t.error} error />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ToolTraceView;
